/**
 * A half-written message, for as long as the page is open.
 *
 * The composer is not always mounted for as long as somebody is writing in it.
 * The inbox moves the thread between the row it was tapped on and the pane
 * beside the list when a tablet crosses the two-pane breakpoint, the customer
 * pages reuse one composer across customers, and a component that changes
 * parents is a component that remounts — taking its `useState` with it. Every
 * one of those is a sentence somebody was halfway through, gone with no
 * warning and no way back.
 *
 * So the sentence lives here instead, keyed by who it is being written to, and
 * outlives any particular mounting of the box it is typed into.
 *
 * MEMORY ONLY, deliberately. Nothing about a half-typed message needs to
 * survive a reload: the requirement is that it survives a re-render. Message
 * bodies are customer data, and the quote drafts that DO go to IndexedDB carry
 * a whole apparatus of scopes, fences and cross-tab wipes to hold that safely
 * (`draft-store.ts`). A store that dies with the page needs none of it — but it
 * still must not outlive the account that typed into it, so it is wiped on the
 * same sign-out fence, and while an administrator is impersonating somebody it
 * refuses to keep anything at all.
 */

export interface MessageDraft {
  channel: 'email' | 'sms' | 'internal';
  subject: string;
  body: string;
  recipientEmail: string;
  recipientPhone: string;
}

/**
 * Enough for a morning's worth of half-finished replies, not enough to be a
 * quiet accumulation of customer messages in memory. The oldest goes first,
 * and a draft is only ever dropped from the middle of a list nobody is looking
 * at: the one being typed into was just written, so it is always the newest.
 */
const MAX_DRAFTS = 20;

const drafts = new Map<string, MessageDraft>();
const sendIdentities = new Map<string, string>();
export function messageDraftSendIdentity(key: string | null): string {
  return key ? (sendIdentities.get(key) ?? crypto.randomUUID()) : crypto.randomUUID();
}
let writesSuspended = false;
let authorizationScope: string | null = null;

/** Memory drafts belong to the current viewer, location and API session. */
export function configureMessageDraftScope(identity: string): void {
  if (authorizationScope !== identity) {
    drafts.clear();
    authorizationScope = identity;
  }
}

/** Nothing is written for a composer that has nobody to write to. */
export function readMessageDraft(key: string | null): MessageDraft | null {
  if (!key || writesSuspended) return null;
  return drafts.get(key) ?? null;
}

export function writeMessageDraft(key: string | null, draft: MessageDraft): void {
  if (!key || writesSuspended) return;
  // Deleted before it is set, so re-writing a key moves it to the end: the
  // eviction below then takes the least recently typed in rather than whatever
  // happened to be opened first.
  const previous = drafts.get(key);
  if (!sendIdentities.has(key) || JSON.stringify(previous) !== JSON.stringify(draft))
    sendIdentities.set(key, crypto.randomUUID());
  drafts.delete(key);
  drafts.set(key, draft);
  if (drafts.size > MAX_DRAFTS) {
    const oldest = drafts.keys().next();
    if (!oldest.done) {
      drafts.delete(oldest.value);
      sendIdentities.delete(oldest.value);
    }
  }
}

export function clearMessageDraft(key: string | null): void {
  if (key) {
    drafts.delete(key);
    sendIdentities.delete(key);
  }
}

export function clearAllMessageDrafts(): void {
  drafts.clear();
  sendIdentities.clear();
}

/**
 * The fence and the wipe are one act here.
 *
 * For a store that only exists in memory there is nothing left to fence off
 * once it is empty, and refusing later writes is what keeps a composer still
 * mounted on the way to the sign-in screen from putting the last account's
 * sentence back.
 */
export function suspendMessageDraftWrites(): void {
  writesSuspended = true;
  clearAllMessageDrafts();
}

export function resumeMessageDraftWrites(): void {
  writesSuspended = false;
}
